import { useState } from 'react';
import { Check, RotateCcw } from 'lucide-react';
import { useSettings } from '../context/SettingsContext';
import { useT } from '../i18n';

/** The default Eveil green plus the eight deck-category colours. */
const PRESETS = [
  '#1d7868', '#e0524d', '#f0c83d', '#f3a8b8',
  '#ee9a3a', '#5dc7a9', '#2e9b6a', '#4677a8',
];
const DEFAULT_ACCENT = '#1d7868';

/**
 * Admin accent picker: presets from the deck's own palette plus a free
 * colour input. The choice applies live app-wide (both themes) and is
 * stored as the app setting `accent_color`.
 */
export function AccentSettings() {
  const { t } = useT();
  const { accentColor, setAccentColor } = useSettings();
  const [custom, setCustom] = useState(accentColor ?? DEFAULT_ACCENT);
  const [saving, setSaving] = useState(false);

  const current = accentColor ?? DEFAULT_ACCENT;

  const pick = async (hex: string | null) => {
    setSaving(true);
    try {
      await setAccentColor(hex);
      if (hex) setCustom(hex);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="card" style={{ marginTop: 16 }}>
      <h3 className="card-title mb-md">{t('admin.accent.title')}</h3>
      <p className="text-sm text-muted" style={{ marginBottom: 12 }}>{t('admin.accent.help')}</p>

      <div className="accent-swatches" role="radiogroup" aria-label={t('admin.accent.title')}>
        {PRESETS.map((hex) => (
          <button
            key={hex}
            type="button"
            role="radio"
            aria-checked={current.toLowerCase() === hex}
            className={`accent-swatch ${current.toLowerCase() === hex ? 'accent-swatch--active' : ''}`}
            style={{ background: hex }}
            title={hex}
            disabled={saving}
            onClick={() => pick(hex)}
          >
            {current.toLowerCase() === hex && <Check size={14} strokeWidth={3} />}
          </button>
        ))}
      </div>

      <div className="accent-custom">
        <label className="accent-custom-label">
          {t('admin.accent.custom')}
          <input
            type="color"
            value={custom}
            disabled={saving}
            onChange={(e) => setCustom(e.target.value)}
            onBlur={() => { if (custom.toLowerCase() !== current.toLowerCase()) pick(custom); }}
          />
          <span className="accent-custom-hex">{custom}</span>
        </label>
        {accentColor && (
          <button type="button" className="btn btn-ghost btn-sm" disabled={saving} onClick={() => pick(null)}>
            <RotateCcw size={13} /> {t('admin.accent.reset')}
          </button>
        )}
      </div>
    </div>
  );
}
